import { z } from 'zod';
import { DecimalSchema } from './common';

/**
 * The fiscal *shape* of the product, with no country's vocabulary in it.
 *
 * This module used to be the model of Argentine tax law: ARCA's fourteen IVA
 * conditions, its four rates, its twenty-four provinces, its five invoice
 * letters, the CUIT check digit. Everything the interface and the API knew
 * about taxes came from here, and it was correct — for one country.
 *
 * It was also the reason a second country could not be added. A `z.enum` over
 * ARCA's values rejects `RUT`, rejects `AFECTO`, and has no letter to offer a
 * Chilean invoice, so a Chilean workspace could not have been described at
 * all. Worse, the duplicate was silent: `@arriba.digital/countries` holds the same
 * functions and tables per country, and the two copies were free to drift.
 *
 * So the vocabulary moved out. It now lives in the country pack, which is the
 * only thing that can differ per country, and the interface asks its workspace
 * for it — see `CountryProfile` in `./country`. What stays here is the shape
 * that is the same everywhere: a code is a short string, a rate is a
 * percentage, an amount is a fixed-scale decimal.
 *
 * The bounds below are deliberately loose. They exist to stop a megabyte of
 * text reaching a database column and to keep a rate parseable, not to
 * enumerate a country's values — enumerating is exactly what went wrong.
 * Rejecting an unknown *code* is the pack's job, and the API does it against
 * the pack of the workspace's own country.
 */

/* ------------------------------------------------------------------ *
 * Codes
 * ------------------------------------------------------------------ */

/**
 * How a taxpayer is identified: `CUIT`, `CUIL`, `DNI`, `RUT`, `CNPJ`, `NONE`.
 *
 * Which of those are legal, whether a value is required, whether it has to be
 * unique, and whether it is well formed are all the pack's answers —
 * `pack.identity.types`.
 */
export const TaxIdTypeSchema = z.string().trim().min(1).max(32);
export type TaxIdType = z.infer<typeof TaxIdTypeSchema>;

/**
 * A taxpayer's standing for the country's tax.
 *
 * `RESPONSABLE_INSCRIPTO` and `MONOTRIBUTISTA` in Argentina, `AFECTO` and
 * `NO_AFECTO` in Chile, `RESPONSABLE_IVA` in Colombia — `pack.tax.regimes`.
 */
export const IvaConditionSchema = z.string().trim().min(1).max(64);
export type IvaCondition = z.infer<typeof IvaConditionSchema>;

/**
 * Whether a zero-rate operation is exempt or outside the scope of the tax.
 *
 * `GRAVADO` / `EXENTO` / `NO_GRAVADO` in Argentina, `AFECTO` / `EXENTO` in
 * Chile — `pack.tax.treatments`.
 */
export const IvaTreatmentSchema = z.string().trim().min(1).max(64);
export type IvaTreatment = z.infer<typeof IvaTreatmentSchema>;

/**
 * A tax rate, as the percentage the country publishes.
 *
 * A string, not a number: `"10.5"` must not become `10.499999999999998` on its
 * way to a database, and a rate is a code that happens to look like a number
 * rather than a quantity to do arithmetic on. The amount is computed once, in
 * `Decimal`, from the base. The digits before the point are bounded at three
 * because no country's VAT exceeds 100 % — anything longer is a typo, not a
 * rate — and the column stores two decimals, so more than two are refused
 * here rather than silently rounded there.
 */
export const IvaRateSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, { message: 'validation.rate' });
export type IvaRate = z.infer<typeof IvaRateSchema>;

/**
 * A fiscal document class: `FACTURA`, `NOTA_DE_CREDITO`, a DTE type code.
 *
 * `pack.documents.types`. The Argentine invoice *letter* has no schema here
 * because it is not a general concept: ARCA encodes it in the numeric type
 * code, and no other country in scope prints one.
 */
export const FiscalDocumentTypeSchema = z.string().trim().min(1).max(64);
export type FiscalDocumentType = z.infer<typeof FiscalDocumentTypeSchema>;

/**
 * The class a user picks on a document, when the country distinguishes one.
 *
 * Kept as a separate name from {@link FiscalDocumentTypeSchema} because the
 * two are asked for at different moments — the class is chosen before posting,
 * the type is resolved from it — even though both are now the pack's code.
 */
export const FiscalDocumentFamilySchema = FiscalDocumentTypeSchema;
export type FiscalDocumentFamily = FiscalDocumentType;

/* ------------------------------------------------------------------ *
 * Tenant fiscal profile
 * ------------------------------------------------------------------ */

/**
 * The company's own fiscal identity, stored under the `fiscal` tenant setting
 * (see `SETTING_KEYS`).
 *
 * The named fields are the ones every country has an opinion about: a legal
 * name, an identifier, a tax standing, a point of sale. Everything else is
 * country data — Argentina's monotributo category and Ingresos Brutos rate,
 * Chile's economic activity code — and passes through untouched, because the
 * core has no business rejecting a field it does not understand.
 *
 * Which of them the interface actually offers is `pack.settings.fields`; this
 * schema only says what shape a value may take.
 */
export const FiscalProfileSchema = z
  .object({
    /** Registered legal name; falls back to the workspace name. */
    legalName: z.string().trim().max(200).optional(),
    taxIdType: TaxIdTypeSchema.optional(),
    taxId: z.string().trim().max(32).nullable().optional(),
    ivaCondition: IvaConditionSchema.optional(),
    /** Point of sale used for fiscal numbering, when the country has one. */
    pointOfSale: z.number().int().min(0).max(9999).optional(),
    /** Jurisdiction code — a province, region, state or department. */
    province: z.string().trim().max(16).optional(),
    fiscalAddress: z.string().trim().max(500).nullable().optional(),
    /** ISO date the activity started with the tax authority. */
    activityStartDate: z.string().trim().max(10).nullable().optional(),
  })
  .catchall(z.union([z.string(), z.number(), z.boolean(), z.null()]));

export type FiscalProfile = z.infer<typeof FiscalProfileSchema>;

/**
 * A percentage stored in the fiscal profile, as a fixed-scale decimal string.
 *
 * Re-exported so the country-specific rate fields a pack defines (withholding
 * rates, municipal rates) validate the same way the ones above do.
 */
export const FiscalRateSchema = DecimalSchema;

/* ------------------------------------------------------------------ *
 * Validation helpers
 * ------------------------------------------------------------------ */

/**
 * A tax identifier as typed by a human, before the pack normalizes it.
 *
 * The shape is checked, the *value* is not: whether eleven digits are a valid
 * CUIT or nine are a valid RUT is the pack's `validate`. This only refuses
 * something that could not be an identifier in any country.
 */
export const taxIdField = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .refine((value) => /[\p{L}\p{N}]/u.test(value), { message: 'validation.taxId' });
